import React, { useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Image, Button, Row, Col, message, Tag } from 'antd';
import { ArrowLeftOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation } from '@apollo/client';
import { GET_DRIVER_VERIFICATION_DETAIL } from '../../../graphql/queries/driverVerification/getDriverVerificationQueries';
import { REVIEW_DRIVER_VERIFICATION } from '../../../graphql/mutations/driverVerification/driverVerificationMutations';
import ReviewModal from './components/ReviewModal';
import { useVehicleTypeOptions } from '../../../hooks/useVehicleTypeOptions';

const DriverVerificationDetailPage: React.FC = () => {
    const { t } = useTranslation();
    const { driverAccountId } = useParams<{ driverAccountId: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const vehicleTypeOptions = useVehicleTypeOptions();
    
    const [reviewModalVisible, setReviewModalVisible] = useState(false);
    const [reviewResult, setReviewResult] = useState<'APPROVED' | 'REJECTED' | null>(null);
    
    const { data, loading, refetch } = useQuery(GET_DRIVER_VERIFICATION_DETAIL, {
        variables: {
            driverAccountId: driverAccountId ? Number(driverAccountId) : 0,
        },
        skip: !driverAccountId,
        fetchPolicy: 'network-only',
    });
    
    const [reviewDriver, { loading: reviewLoading }] = useMutation(REVIEW_DRIVER_VERIFICATION);
    
    const detail = data?.getDriverVerificationDetail;
    
    // 处理审核按钮点击
    const handleApproveClick = () => {
        setReviewResult('APPROVED');
        setReviewModalVisible(true);
    };
    
    const handleRejectClick = () => {
        setReviewResult('REJECTED');
        setReviewModalVisible(true);
    };
    
    // 处理审核提交
    const handleReviewSubmit = async (reviewResult: 'APPROVED' | 'REJECTED', reviewComment?: string) => {
        if (!driverAccountId) {
            message.error(t('riskControl.driverVerification.messages.selectReviewResult'));
            return;
        }
        
        try {
            await reviewDriver({
                variables: {
                    driverAccountId: driverAccountId,
                    reviewResult: reviewResult,
                    reviewComment: reviewComment,
                },
            });
            message.success(t('riskControl.driverVerification.messages.reviewSuccess'));
            setReviewModalVisible(false);
            setReviewResult(null);
            refetch();
            // 保持筛选状态，从当前URL的查询参数中获取
            const searchParams = location.search;
            navigate(`/qualitycontrol/driver-verification${searchParams}`);
        } catch (error: any) {
            message.error(error.message || t('riskControl.driverVerification.messages.reviewFailed'));
        }
    };
    
    // 图片URL已经由后端GraphQL返回，直接使用
    
    if (loading) {
        return <div className="p-6">Loading...</div>;
    }
    
    if (!detail) {
        return <div className="p-6">Driver not found</div>;
    }
    
    // 格式化手机号（带区号）
    const formatPhone = (phone: string, countryCode?: number) => {
        if (countryCode && countryCode !== 0) {
            return `+${countryCode} ${phone}`;
        }
        return phone;
    };
    
    // 标题：司机ID———电话号(姓名) - 使用三个短横线
    const formattedPhone = formatPhone(detail.phone, detail.countryCode);
    const pageTitle = `${detail.driverId}———${formattedPhone}(${detail.name})`;
    
    return (
        <div className="bg-white min-h-screen p-6">
            {/* 顶部：返回按钮和标题 */}
            <div className="flex items-center justify-between mb-8">
                <div className="flex items-center">
                    <Button
                        type="text"
                        icon={<ArrowLeftOutlined />}
                        onClick={() => {
                            // 保持筛选状态，从当前URL的查询参数中获取
                            const searchParams = location.search;
                            navigate(`/qualitycontrol/driver-verification${searchParams}`);
                        }}
                        className="mr-4"
                    />
                    <h1 className="text-lg font-semibold m-0">{pageTitle}</h1>
                </div>
                
                {/* 右上角操作按钮（垂直排列） */}
                <div className="flex flex-col gap-2">
                    <Button
                        type="primary"
                        icon={<CheckOutlined />}
                        onClick={handleApproveClick}
                        className="bg-green-500 hover:bg-green-600 border-green-500 text-white"
                        style={{ minWidth: '100px' }}
                    >
                        {t('riskControl.driverVerification.reviewModal.approve')}
                    </Button>
                    <Button
                        danger
                        icon={<CloseOutlined />}
                        onClick={handleRejectClick}
                        style={{ minWidth: '100px' }}
                    >
                        {t('riskControl.driverVerification.reviewModal.reject')}
                    </Button>
                </div>
            </div>
            
            {/* 上半部分：司机信息（左右两列布局） */}
            <Row gutter={32} className="mb-8">
                {/* 左列：司机基本信息 */}
                <Col span={12}>
                    <div className="space-y-5">
                        <div className="flex items-center">
                            <span className="w-28 text-gray-600">{t('riskControl.driverVerification.detail.driverId')}:</span>
                            <span className="text-gray-900 flex-1">{detail.driverId}</span>
                        </div>
                        <div className="flex items-center">
                            <span className="w-28 text-gray-600">{t('riskControl.driverVerification.detail.driverName')}:</span>
                            <span className="text-gray-900 flex-1">{detail.name}</span>
                        </div>
                        <div className="flex items-center">
                            <span className="w-28 text-gray-600">{t('riskControl.driverVerification.detail.driverPhone')}:</span>
                            <span className="text-gray-900 flex-1">{formattedPhone}</span>
                        </div>
                        <div className="flex items-center">
                            <span className="w-28 text-gray-600">{t('riskControl.driverVerification.detail.affiliatedFleet')}:</span>
                            <span className="text-gray-900 flex-1">{detail.dspName || '-'}</span>
                        </div>
                    </div>
                </Col>
                
                {/* 右列：证件和车辆信息 */}
                <Col span={12}>
                    <div className="space-y-5">
                        <div className="flex items-center">
                            <span className="w-36 text-gray-600">{t('riskControl.driverVerification.detail.idDocumentNumber')}:</span>
                            <span className="text-gray-900 flex-1">{detail.idDocumentNumber || '-'}</span>
                        </div>
                        <div className="flex items-center">
                            <span className="w-36 text-gray-600">{t('riskControl.driverVerification.detail.licensePlateNumber')}:</span>
                            <span className="text-gray-900 flex-1">{detail.vehiclePlate || '-'}</span>
                        </div>
                    </div>
                </Col>
            </Row>
            
            {/* 下半部分：照片区域（左右两列布局） */}
            <Row gutter={32}>
                {/* 左列：驾照照片（证件照片） */}
                <Col span={12}>
                    <div>
                        <h3 className="text-base font-semibold mb-4">
                            {t('riskControl.driverVerification.detail.idDocumentPhotos')}
                        </h3>
                        {detail.idDocumentImageUrls && detail.idDocumentImageUrls.length > 0 ? (
                            <div className="grid grid-cols-2 gap-3">
                                {detail.idDocumentImageUrls.map((url: string, index: number) => (
                                    <div key={index} className="aspect-square border border-gray-300 rounded overflow-hidden bg-gray-50">
                                        <Image
                                            src={url}
                                            alt={`ID Document ${index + 1}`}
                                            className="w-full h-full object-cover"
                                            preview={{
                                                mask: <div className="flex items-center justify-center text-white text-sm">点击预览</div>
                                            }}
                                        />
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-gray-400 py-8 text-center">{t('common.noPhotosAvailable')}</div>
                        )}
                    </div>
                </Col>
                
                {/* 右列：车辆照片 */}
                <Col span={12}>
                    <div>
                        <h3 className="text-base font-semibold mb-4">
                            {t('riskControl.driverVerification.detail.vehiclePhotos')}
                        </h3>
                        {detail.vehicleImageUrls && detail.vehicleImageUrls.length > 0 ? (
                            <div className="grid grid-cols-2 gap-3">
                                {detail.vehicleImageUrls.map((url: string, index: number) => (
                                    <div key={index} className="aspect-square border border-gray-300 rounded overflow-hidden bg-gray-50">
                                        <Image
                                            src={url}
                                            alt={`Vehicle ${index + 1}`}
                                            className="w-full h-full object-cover"
                                            preview={{
                                                mask: <div className="flex items-center justify-center text-white text-sm">点击预览</div>
                                            }}
                                        />
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-gray-400 py-8 text-center">{t('common.noPhotosAvailable')}</div>
                        )}
                    </div>
                </Col>
            </Row>
            
            {/* 审核弹窗 */}
            {reviewModalVisible && (
                <ReviewModal
                    visible={reviewModalVisible}
                    driverAccountId={driverAccountId ? Number(driverAccountId) : null}
                    reviewResult={reviewResult || undefined}
                    onCancel={() => {
                        setReviewModalVisible(false);
                        setReviewResult(null);
                    }}
                    onSubmit={handleReviewSubmit}
                    loading={reviewLoading}
                />
            )}
            
        </div>
    );
};

export default DriverVerificationDetailPage;
